import React from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { projectsApi, authApi, type Project } from '../lib/api'
import { Button } from './ui/Button'
import { FolderOpen, Plus, Home, User, Settings } from 'lucide-react'
import { USERS_SETTINGS_PATH, useCanManageUsersSettings } from '../lib/usersSettings'

interface SidebarProps {
  onCreateProject?: () => void
}

export const Sidebar: React.FC<SidebarProps> = ({ onCreateProject }) => {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const showUsersSettings = useCanManageUsersSettings()

  // Fetch all projects for sidebar
  const { data: projectsData, isLoading: projectsLoading, error: projectsError } = useQuery(
    ['projects', { limit: 20 }],
    () => projectsApi.list({ limit: 20 }),
    {
      keepPreviousData: true,
    }
  )

  // Fetch current user info
  const { data: currentUser, isLoading: userLoading } = useQuery(
    ['currentUser'],
    () => authApi.getCurrentUser(),
    {
      retry: 1,
    }
  )

  const projects = Array.isArray(projectsData?.data) 
    ? projectsData.data 
    : Array.isArray((projectsData as any)?.projects)
    ? (projectsData as any).projects
    : []

  // Determine selected project from URL
  const match = location.pathname.match(/\/projects\/([\w-]+)/)
  const selectedProject = match ? match[1] : null

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-screen flex flex-col relative">
      {/* Header */}
      <div className="p-6 pb-0 flex-shrink-0">
        <div className="mb-6">
          <div className="flex items-baseline space-x-1">
            <img
              src="/propalyst-logo.svg"
              alt="Darwin logo"
              className="h-4 w-auto"
            />
            <span className="text-xs font-medium text-gray-500" style={{ marginLeft: '1px' }}>
              .bidchecker
            </span>
          </div>
        </div>
        <Button
          className="w-full bg-black text-white hover:bg-gray-800"
          onClick={onCreateProject || (() => navigate('/dashboard'))}
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Project
        </Button>
      </div>

      {/* Navigation - fixed above the divider */}
      <div className="px-6 py-4 border-b flex-shrink-0">
        <div
          className={`group flex items-center space-x-3 px-3 py-3 rounded-md cursor-pointer hover:bg-gray-50 ${location.pathname === '/dashboard' ? 'bg-purple-50' : ''}`}
          onClick={() => navigate('/dashboard')}
        >
          <Home className={`w-4 h-4 ${location.pathname === '/dashboard' ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-900'}`} />
          <span className="text-sm font-semibold text-gray-900">Home</span>
        </div>
        {showUsersSettings && (
          <Link
            to={USERS_SETTINGS_PATH}
            className={`group flex items-center space-x-3 px-3 py-3 rounded-md hover:bg-gray-50 ${location.pathname === USERS_SETTINGS_PATH ? 'bg-purple-50' : ''}`}
          >
            <Settings className={`w-4 h-4 ${location.pathname === USERS_SETTINGS_PATH ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-900'}`} />
            <span className="text-sm font-semibold text-gray-900">Users</span>
          </Link>
        )}
      </div>

      {/* Projects List - only this area scrolls */}
      <div className="flex-1 min-h-0 flex flex-col px-6 pt-4">
        <div className="flex items-center mb-4 px-3 py-3 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <FolderOpen className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-semibold text-gray-900">Projects ({projects.length})</span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto space-y-2 ml-6 pb-20">
            {projectsLoading ? (
              <div className="px-3 py-3 text-sm text-gray-500">
                Loading projects...
              </div>
            ) : projectsError ? (
              <div className="px-3 py-3 text-sm text-red-500">
                Error loading projects
              </div>
            ) : projects.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500">
                No projects found
              </div>
            ) : (
              <>
                {projects.map((proj: Project) => (
                  <div
                    key={proj.id}
                    className={`w-full text-left px-3 py-3 rounded-md transition-colors cursor-pointer hover:bg-gray-50 hover:text-gray-900 ${
                      selectedProject === proj.id
                        ? 'bg-purple-50 text-gray-900'
                        : 'text-gray-600'
                    }`}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      navigate(`/projects/${proj.id}`)
                    }}
                  >
                    <div className="text-sm font-medium">{proj.title}</div>
                  </div>
                ))}
              </>
            )}
        </div>
      </div>

      {/* Fixed User Info Section at bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        {userLoading ? (
          <div className="flex items-center space-x-3 px-3 py-2">
            <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="flex-1">
              <div className="h-3 bg-gray-200 rounded animate-pulse mb-1"></div>
              <div className="h-2 bg-gray-200 rounded animate-pulse w-3/4"></div>
            </div>
          </div>
        ) : currentUser ? (
          <div className="flex items-center space-x-3 px-3 py-2">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">
                {currentUser.first_name && currentUser.last_name 
                  ? `${currentUser.first_name} ${currentUser.last_name}`
                  : currentUser.email}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {currentUser.current_tenant?.name || 'No tenant'}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-3 px-3 py-2">
            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-gray-400" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-500">Not logged in</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
} 